import type { ImportJob, ImportProgress, ImportSummary, ImportTemplate, ImportJobsResponse } from '@/types/import';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

const getApiBaseUrl = (): string => {
  const baseUrl = getApiUrl() || getBackendUrl();
  return `${baseUrl}/api/Import`;
};

// Custom fetch function that ensures authorization header is added
const importFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const headers = new Headers(init?.headers);
  
  const authConfig = getAuthConfig();
  
  if (authConfig.getAccessToken) {
    try {
      const token = await authConfig.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
  }

  // Always set X-Instance-Id header, with fallback if not configured
  const instanceId = authConfig.instanceId ?? 'default-instance';
  headers.set('X-Instance-Id', instanceId);

  return fetch(input, {
    ...init,
    headers,
  });
};

export const importService = {
  // Get CSV template structure
  async getTemplate(): Promise<ImportTemplate> {
    const response = await importFetch(`${getApiBaseUrl()}/template`);
    if (!response.ok) throw new Error('Failed to fetch template');
    return response.json();
  },

  // Upload CSV file and start import job
  async uploadFile(file: File): Promise<ImportJob> {
    const formData = new FormData();
    formData.append('file', file);

    const uploadResponse = await importFetch(`${getApiBaseUrl()}/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) throw new Error('Failed to upload file');
    const job = await uploadResponse.json();

    // Start the import job after uploading
    const startResponse = await importFetch(`${getApiBaseUrl()}/${job.id}/start`, {
      method: 'POST',
    });

    if (!startResponse.ok) throw new Error('Failed to start import');
    return startResponse.json();
  },

  // Get progress of a specific import job
  async getProgress(jobId: string): Promise<ImportProgress> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/progress`);
    if (!response.ok) throw new Error('Failed to fetch progress');
    return response.json();
  },

  // Get details of a specific import job
  async getJob(jobId: string): Promise<ImportJob> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}`);
    if (!response.ok) throw new Error('Failed to fetch job details');
    return response.json();
  },

  // Get all import jobs
  async getJobs(): Promise<ImportJobsResponse> {
    const response = await importFetch(`${getApiBaseUrl()}/jobs`);
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return response.json();
  },

  // Cancel an import job
  async cancelJob(jobId: string): Promise<void> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to cancel job');
  },

  // Get import summary
  async getSummary(jobId: string): Promise<ImportSummary> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/summary`);
    if (!response.ok) throw new Error('Failed to fetch summary');
    return response.json();
  },

  // Download error report
  async downloadErrorReport(jobId: string): Promise<Blob> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}/error-report`);
    if (!response.ok) throw new Error('Failed to download error report');
    return response.blob();
  },

  // Delete a specific import job
  async deleteJob(jobId: string): Promise<void> {
    const response = await importFetch(`${getApiBaseUrl()}/${jobId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message ?? 'Failed to delete job');
    }
  },

  async clearHistory(): Promise<{ deletedCount: number }> {
    const response = await importFetch(`${getApiBaseUrl()}/history/clear`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to clear history');
    return response.json();
  },

  // Generate and download CSV template from backend API (headers only, no sample data)
  async downloadTemplateCSV(): Promise<void> {
    try {
      const template = await this.getTemplate();
      
      // Generate headers from template fields (using Label for user-friendly names)
      const headers = template.fields.map(field => field.label);

      // Escape CSV values that contain commas or quotes
      const escapeCSV = (value: string): string => {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      // Create CSV with headers only (no sample data row)
      const csvContent = headers.map(escapeCSV).join(',') + '\n';

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', 'asset_import_template.csv');
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
      throw new Error('Failed to download CSV template. Please try again.');
    }
  },

  // Legacy synchronous method for backward compatibility (uses hardcoded template, headers only)
  generateTemplateCSV(): void {
    console.warn('generateTemplateCSV is deprecated. Use downloadTemplateCSV() instead for dynamic template from backend.');
    
    // Fallback to hardcoded template for backward compatibility (headers only)
    const headers = [
      'Asset Code', 'Asset Name', 'Category', 'Sub Category', 'Location', 'Status',
      'Acquisition Cost', 'Acquisition Date', 'Currency', 'Quantity', 'Serial Number',
      'Manufacturer', 'Model', 'Barcode', 'Barcode Type', 'Barcode Value',
      'Resource Code', 'Sub Ledger', 'Registration Number', 'Description',
      'Net Book Value', 'Accumulated Depreciation', 'Accumulated Additions',
      'Accumulated Disposals', 'Accumulated Revaluation', 'NBV at Start of Year', 'NBV at End of Year',
      'Assigned To', 'Assigned Date',
      'Depreciation Method', 'Depreciation Percentage', 'Useful Life (Years)', 'Residual Value',
      'Posting Frequency', 'Do Not Depreciate', 'In Service Period', 'In Service Year', 'Base Value On',
      'Asset Cost Nominal', 'Depreciation BS Nominal', 'Depreciation PL Nominal', 'Disposals Nominal',
      'Revaluation BS Nominal', 'Revaluation PL Nominal', 'Disposals Bank Account Nominal', 'Additions WIP Bank Account Nominal',
      'Project', 'Cost Centre',
      'Supplier Name', 'Supplier Code', 'Purchase Order Number', 'Invoice Reference', 'Hire Purchase',
      'Insurance Policy Number', 'Insurance Provider', 'Insurance Start Date', 'Insurance Expiry Date', 'Insurance Value', 'Insurance Notes',
      'Warranty Provider', 'Warranty Start Date', 'Warranty Expiry Date', 'Warranty Terms', 'Warranty Notes',
      'User Key 1', 'User Key 2', 'User Key 3', 'User Key 4', 'User Key 5',
      'User Value 1', 'User Value 2', 'User Value 3', 'User Value 4', 'User Value 5',
      'User Date 1', 'User Date 2', 'User Date 3',
      'Notes'
    ];

    // Headers only, no sample data
    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'asset_import_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
